import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Icon } from "../../components/Icon.jsx";
import { ConfidenceBar, StatusPill } from "../../components/ui.jsx";

/** Right-click menu, positioned with logical insets so it mirrors correctly in RTL. */
export function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ start: x, top: y });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rtl = document.documentElement.dir === "rtl";
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let start = rtl ? vw - x : x;
    if (start + w > vw - 8) start = Math.max(8, vw - w - 8);
    let top = y;
    if (top + h > vh - 8) top = Math.max(8, vh - h - 8);
    setPos({ start, top });
  }, [x, y]);

  useEffect(() => {
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && onClose();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", onClose);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onClose);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  useEffect(() => {
    ref.current?.querySelector("button:not([disabled])")?.focus();
  }, []);

  return (
    <div
      ref={ref}
      className="ctx-menu"
      role="menu"
      style={{ insetInlineStart: pos.start, insetBlockStart: pos.top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((it, i) =>
        it.separator ? (
          <div key={`sep-${i}`} className="ctx-sep" role="separator" />
        ) : (
          <button
            key={it.id}
            type="button"
            role="menuitem"
            className={`ctx-item ${it.danger ? "is-danger" : ""}`}
            disabled={it.disabled}
            onClick={() => {
              onClose();
              it.onSelect?.();
            }}
          >
            <Icon name={it.icon} size={15} />
            <span className="ctx-label">{it.label}</span>
            {it.tag ? <span className="soon-tag">{it.tag}</span> : null}
            {it.hint ? <span className="ctx-hint">{it.hint}</span> : null}
          </button>
        ),
      )}
    </div>
  );
}

export function FindingsTable({ findings, selected, activeId, onToggle, onToggleAll, onActivate, onContextMenu }) {
  const intl = useIntl();
  const allChecked = findings.length > 0 && findings.every((f) => selected.has(f.id));
  const someChecked = findings.some((f) => selected.has(f.id));
  const headRef = useRef(null);
  useEffect(() => {
    if (headRef.current) headRef.current.indeterminate = someChecked && !allChecked;
  }, [someChecked, allChecked]);

  return (
    <div className="grid-wrap results-grid">
      <table className="grid grid-dense">
        <thead>
          <tr>
            <th className="col-check">
              <input
                ref={headRef}
                type="checkbox"
                checked={allChecked}
                onChange={() => onToggleAll(!allChecked)}
                aria-label={intl.formatMessage({ id: "results.selectAll" })}
              />
            </th>
            <th className="col-type">
              <FormattedMessage id="results.col.type" />
            </th>
            <th>
              <FormattedMessage id="results.col.value" />
            </th>
            <th className="col-conf">
              <FormattedMessage id="results.col.confidence" />
            </th>
            <th className="col-review">
              <FormattedMessage id="results.col.review" />
            </th>
            <th className="col-ev">
              <FormattedMessage id="results.col.evidence" />
            </th>
          </tr>
        </thead>
        <tbody>
          {findings.map((f) => (
            <tr
              key={f.id}
              className={`${selected.has(f.id) ? "is-checked" : ""} ${activeId === f.id ? "is-active" : ""}`}
              onClick={() => onActivate(f.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                onContextMenu(e, f);
              }}
            >
              <td className="col-check" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(f.id)}
                  onChange={() => onToggle(f.id)}
                  aria-label={intl.formatMessage({ id: "results.selectRow" }, { value: f.value })}
                />
              </td>
              <td className="col-type">
                <span className={`type-badge ftype-${f.type}`}>
                  <FormattedMessage id={`findingType.${f.type}`} defaultMessage={f.type} />
                </span>
              </td>
              <td className="col-value">
                <span className="mono value-text">{f.value}</span>
                {f.note ? (
                  <span className="note-flag" title={f.note}>
                    <Icon name="note" size={13} />
                  </span>
                ) : null}
              </td>
              <td className="col-conf">
                <ConfidenceBar value={f.confidence} />
              </td>
              <td className="col-review">
                <StatusPill ns="review" value={f.reviewState || "unreviewed"} />
              </td>
              <td className="col-ev mono muted">{f.evidenceIds?.length || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
